import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AnimatedScore } from '@/components/matches/AnimatedScore';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { LiveBadge } from '@/components/ui/LiveBadge';
import { ProbabilityBar } from '@/components/ui/ProbabilityBar';
import { useThemePalette } from '@/config/theme';
import type { MatchProbabilities } from '@/features/predictions/model';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { formatKickoffTime } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeCompetition, localizeTeamName } from '@/lib/i18n/footballNames';
import type { Locale } from '@/lib/i18n';
import { isHotMatch } from '@/lib/utils/popularTeams';

function HeroSide({ team, locale }: { team: FixtureWithRefs['home_team']; locale: Locale }) {
  return (
    <View className="flex-1 items-center gap-2">
      <TeamCrest uri={team?.logo_url} name={localizeTeamName(team, locale)} size="lg" />
      <AppText variant="caption" weight="bold" className="text-center text-ink" numberOfLines={1}>
        {localizeTeamName(team, locale, true)}
      </AppText>
    </View>
  );
}

/** The Volt hero card: today's headline match with the form-model 1X2 bar. */
export function HeroMatchCard({
  fixture,
  probabilities,
}: {
  fixture: FixtureWithRefs;
  probabilities: MatchProbabilities | null;
}) {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const isLive = fixture.status === 'live';
  const showScore = isLive || fixture.status === 'finished';
  const kickoffDate = fixture.kickoff_at ? new Date(fixture.kickoff_at) : null;
  const kickoff = kickoffDate && !Number.isNaN(kickoffDate.getTime()) ? kickoffDate : null;

  return (
    <Pressable accessibilityRole="button" onPress={() => router.push(`/match/${fixture.id}`)}>
      <LinearGradient
        colors={[palette.surface, palette.gradMid]}
        style={{
          marginHorizontal: 16,
          borderRadius: 16,
          borderWidth: isLive ? 1.5 : 1,
          borderColor: isLive ? palette.volt : palette.voltLine,
          paddingHorizontal: 16,
          paddingVertical: 16,
          overflow: 'hidden',
          // A soft live glow so the running match stands out as the headline.
          ...(isLive
            ? { shadowColor: palette.volt, shadowOpacity: 0.5, shadowRadius: 16, shadowOffset: { width: 0, height: 0 } }
            : null),
        }}
      >
        <View className="flex-row items-center justify-between">
          <View className="flex-row items-center gap-1.5">
            <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
              {localizeCompetition(fixture.competition?.name, locale)}
            </AppText>
            {isHotMatch(fixture) ? (
              <View
                className="flex-row items-center gap-1 rounded-full bg-volt px-2 py-0.5"
                accessibilityLabel={t('matches.hot')}
              >
                <Ionicons name="star" size={9} color={palette.onVolt} />
                <AppText variant="micro" weight="bold" style={{ color: palette.onVolt, fontSize: 8 }}>
                  {t('matches.featured')}
                </AppText>
              </View>
            ) : null}
          </View>
          {isLive ? <LiveBadge minute={fixture.minute} /> : null}
        </View>

        <View className="flex-row items-center py-3">
          <HeroSide team={fixture.home_team} locale={locale} />
          <View className="w-28 items-center gap-1">
            {showScore ? (
              <AnimatedScore
                home={fixture.home_score ?? 0}
                away={fixture.away_score ?? 0}
                emphasized
              />
            ) : (
              <>
                <AppText variant="heading" weight="bold" className="text-soft">
                  {t('matches.vs').toUpperCase()}
                </AppText>
                <View className="rounded-full bg-volt-glass px-2.5 py-1">
                  <AppText variant="caption" weight="bold" tabular className="text-volt-bright" numberOfLines={1}>
                    {kickoff ? formatKickoffTime(kickoff, numeralOptions) : '—'}
                  </AppText>
                </View>
              </>
            )}
            {fixture.venue ? (
              <AppText variant="micro" className="text-muted" numberOfLines={1}>
                {fixture.venue}
              </AppText>
            ) : null}
          </View>
          <HeroSide team={fixture.away_team} locale={locale} />
        </View>

        {probabilities ? (
          <ProbabilityBar
            probabilities={probabilities}
            homeLabel={t('matches.winShort')}
            drawLabel={t('matches.drawShort')}
            awayLabel={t('matches.winShort')}
          />
        ) : null}
      </LinearGradient>
    </Pressable>
  );
}
